import { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { toast } from "react-toastify";
import { saveAs } from "file-saver";
import { STATUS_CODES } from "@src/config/constant";
import { TravelService } from "@src/services/TravelService";
import { generateTravelQrCode } from "@src/store/slices/travel";
import { getPlanListFromResponse, getTextValue, normalizeTravelPlans, TravelPlanCard } from "./PlanSelection";
import type { TravelPlan } from "./PlanSelection";
import FeatureIncludedPopup from "./popups/FeatureIncludedPopup";

type CheckoutProps = {
    selectedPlanIds: string[];
    selectedPlans?: TravelPlan[];
    travellerList?: any[];
    travellerDobValues?: any[];
    buildCreateLeadPayload?: (travellerList: any[]) => any;
    leadId?: number | null;
    setLoading: (loading: boolean) => void;
};

const getDateValue = (value: any) => (
    value ? new Date(value).toISOString().slice(0, 10) : ""
);

export default function Checkout({
    selectedPlanIds,
    selectedPlans = [],
    travellerList = [],
    travellerDobValues = [],
    buildCreateLeadPayload,
    leadId,
    setLoading,
}: CheckoutProps) {
    const dispatch: any = useDispatch();
    const [selectedFeaturePlan, setSelectedFeaturePlan] = useState<TravelPlan | null>(null);
    const [checkoutPlans, setCheckoutPlans] = useState<TravelPlan[]>([]);
    const selectedApiPlans = checkoutPlans.length ? checkoutPlans : selectedPlans.filter((plan) => plan?.id);
    const selectedPlanIdsByPriority = [...selectedPlanIds].reverse();
    const selectedPlanIdValue = selectedPlanIdsByPriority
        .map((planId) => planId.replace("edit-plan-", ""))
        .find(Boolean);

    const selectedPlan = selectedPlanIdsByPriority
        .map((planId) => selectedApiPlans.find((plan) => plan.id === planId))
        .find(Boolean)
        || (selectedPlanIdValue
            ? selectedApiPlans.find((plan) => String(plan.planId) === String(selectedPlanIdValue))
            : null)
        || selectedApiPlans[selectedApiPlans.length - 1]
        || null;
    const selectedFeatureGroups = selectedFeaturePlan?.featureGroups?.length
        ? selectedFeaturePlan.featureGroups
        : [{
            title: "Features Included",
            items: selectedFeaturePlan?.benefits.map((benefit) => ({ title: benefit })) || [],
        }];

    useEffect(() => {
        let isMounted = true;

        const fetchCheckoutPlan = async () => {
            if (typeof buildCreateLeadPayload !== "function" || !selectedPlanIdValue) {
                setCheckoutPlans([]);
                return;
            }

            const {
                traveller_count,
                trip_start_date,
                trip_end_date,
                insurance_type,
                trip_type,
                origin_country_id,
                dest_country_id,
            } = buildCreateLeadPayload(Array.isArray(travellerList) ? travellerList : []);
            const insured = travellerDobValues
                .map((dobValue: any) => getDateValue(dobValue))
                .filter(Boolean)
                .map((dob: string) => ({ dob }));

            try {
                const response: any = await TravelService.travelPlanList({
                    sort_by: "ASC",
                    traveller_count,
                    trip_start_date,
                    trip_end_date,
                    insurance_type,
                    trip_type,
                    origin_country_id,
                    dest_country_id,
                    insured,
                });
                const responseData = response?.data;
                const apiPlanList = getPlanListFromResponse(responseData?.data ?? responseData);
                const isSuccessResponse = responseData?.status === STATUS_CODES.success || response?.status === STATUS_CODES.success;

                if (!isMounted || (!isSuccessResponse && !apiPlanList.length)) return;

                const normalizedPlans = normalizeTravelPlans(apiPlanList);
                const matchedPlan = normalizedPlans.find((plan) => String(plan.planId) === String(selectedPlanIdValue));
                setCheckoutPlans(matchedPlan ? [matchedPlan] : []);
            } catch (error: any) {
                if (isMounted) {
                    setCheckoutPlans([]);
                    toast.error(getTextValue(error?.message) || "Unable to fetch selected travel plan");
                }
            }
        };

        fetchCheckoutPlan();

        return () => {
            isMounted = false;
        };
    }, [buildCreateLeadPayload, selectedPlanIdValue, travellerDobValues, travellerList]);

    const handleGenerateQrCode = async () => {
        if (!leadId) {
            toast.error("Lead id not found.");
            return;
        }

        setLoading(true);
        try {
            const response: any = await dispatch(generateTravelQrCode({ lead_id: leadId }));
            const qrCode = response?.data?.qr_code;

            if (qrCode) saveAs(qrCode, `qr_code_${response?.data?.lead_id || leadId}.png`);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="travel-checkout">
            <h2 className="heading-h2">Make Payment</h2>
            {selectedPlan ? (
                <TravelPlanCard
                    plan={selectedPlan}
                    actionLabel="Generate QR Code"
                    className="travel-payment-plan-card"
                    onToggle={handleGenerateQrCode}
                    onSeeAll={setSelectedFeaturePlan}
                />
            ) : (
                <div className="travel-plan-empty f-13">Selected plan not available</div>
            )}
            <FeatureIncludedPopup
                isOpen={Boolean(selectedFeaturePlan)}
                onClose={() => setSelectedFeaturePlan(null)}
                title="Features Included"
                features={selectedFeatureGroups}
            />
        </div>
    );
}
